// eslint-disable-next-line @typescript-eslint/no-unused-vars,import/no-unresolved
import { useLocation } from '@reach/router';
import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import type { FooterBaseProps } from './footer-base';
import FooterBase from './footer-base';
import CorporateFooter from './corporate-footer';
import FranchiseFooter from './franchise-footer';
import { FeatureFlag, useFlag } from '../../contexts/events-provider';

const FlagTester = ({ flag, wasIgnored }: { flag: FeatureFlag, wasIgnored: boolean }): JSX.Element => {
    const [show, setShow] = React.useState(false);

    React.useEffect(() => {
        try {
            const shouldShow = localStorage.getItem('flag_show');
            if (shouldShow) {
                setShow(true);
            }
            // eslint-disable-next-line no-empty
        } catch (e) {
        }
    }, []);

    if (wasIgnored) {
        return (
            <div id="fft" style={{ display: show ? 'block' : 'none' }}>
                Ignored - default state
                {' '}
                {flag}
            </div>
        );
    }

    if (flag === FeatureFlag.WaitingForBucketing) {
        return (
            <div id="fft" style={{ display: show ? 'block' : 'none' }}>
                Waiting...
            </div>
        );
    }

    if (flag === FeatureFlag.Control) {
        return (
            <div id="fft" style={{ display: show ? 'block' : 'none' }}>
                Control
            </div>
        );
    }

    if (flag === FeatureFlag.Variant) {
        return (
            <div id="fft" style={{ display: show ? 'block' : 'none' }}>
                Variant
            </div>
        );
    }

    return <div id="fft" style={{ display: show ? 'block' : 'none' }}>No state found</div>;
};

const Footer = (): JSX.Element => {
    const location = useLocation();
    const { isMicrosite, footer, franchise } = useMicrosite();

    const {
        allCsFooterDisclaimers: {
            nodes: dynamicFooterDisclaimers,
        },
        csLegal,
        csFooterNav: defaultFooter,
    } = useStaticQuery<Queries.FooterAncillaryDataQuery>(
        graphql`
            query FooterAncillaryData {
                csLegal {
                    copy
                }
                allCsFooterDisclaimers {
                    nodes {
                        contentBefore
                        content
                        pageMatch
                    }
                }
                csFooterNav {
                  title
                  links {
                    title
                    url
                    links {
                      title
                      url
                      className
                      sublinks {
                        title
                        url
                        sublinks {
                          title
                          url
                        }
                      }
                    }
                  }
                }
            }
        `,
    );

    const { flag, wasIgnored } = useFlag('test_flag');

    const staticDisclaimerCopy = csLegal?.copy || '';
    const franchiseLinks = footer?.links;
    const links = isMicrosite ? franchiseLinks : (defaultFooter?.links || []);

    // Filter links based on DNI provider if needed
    const filteredLinks = links?.map((group) => {
        if (!group) return null;
        return {
            ...group,
            links: group.links?.filter((link): link is NonNullable<typeof link> => link !== null),
        };
    }).filter((group): group is NonNullable<typeof group> => group !== null);

    // Render the appropriate footer variant
    if (isMicrosite) {
        return (
            <>
                <FooterBase
                    isMicrosite
                    links={filteredLinks as FooterBaseProps['links'] || []}
                    staticDisclaimerCopy={staticDisclaimerCopy}
                    dynamicFooterDisclaimers={dynamicFooterDisclaimers}
                    currentPath={location?.pathname}
                    franchise={franchise}
                >
                    <FranchiseFooter franchise={franchise} />
                </FooterBase>
                <FlagTester flag={flag} wasIgnored={wasIgnored} />
            </>
        );
    }

    return (
        <>
            <FooterBase
                isMicrosite={false}
                links={filteredLinks as FooterBaseProps['links'] || []}
                staticDisclaimerCopy={staticDisclaimerCopy}
                dynamicFooterDisclaimers={dynamicFooterDisclaimers}
                currentPath={location?.pathname}
                franchise={null}
            >
                <CorporateFooter />
            </FooterBase>
            <FlagTester flag={flag} wasIgnored={wasIgnored} />
        </>
    );
};

export default Footer;
